import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight,
  FiSearch, FiX, FiSave, FiDownload, FiFolder, FiFileText, FiLayers,
  FiLock, FiTool, FiPrinter, FiBriefcase, FiCreditCard, FiPieChart,
  FiTrendingUp, FiTrendingDown
} from 'react-icons/fi';
import { printChartOfAccounts } from '../../utils/masterPrintHelper';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy } from 'firebase/firestore';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { invalidateMaster } from '../../utils/masterDataStore';
import { isAccountCodeDuplicate, writeAuditLog } from '../../utils/validationHelpers';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { generateAccountCode } from '../../utils/accountHelper';
import { initializeChartOfAccounts, repairChartOfAccounts } from '../../utils/coaInitializer';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';
import { clearSystemAccountMisses } from '../../utils/subTransactionHelper';

const ChartOfAccounts = () => {
  const { userProfile, activeCompany } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'chartOfAccounts');
  const [accounts, setAccounts] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingAccount, setEditingAccount] = useState(null);
  const [parentAccount, setParentAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingDefaults, setCreatingDefaults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    level: 1,
    parentId: null,
    accountType: 'asset',
    balanceType: 'debit',
    description: '',
    linkedEntityType: null,
    linkedEntityId: null,
    isActive: true
  });

  // Keyboard shortcuts: Ctrl+S to submit form when modal is open
  // Note: Ctrl+N is not used here because adding accounts requires selecting a parent first
  useDocumentShortcuts({
    onNew: undefined,
    onSave: () => { const form = document.querySelector('form'); if (form) form.requestSubmit(); },
    viewMode: showModal ? 'form' : 'list',
    saving: false
  });

  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);
      const accountsRef = collection(db, 'companies', activeCompany?.id, 'accounts');
      const q = query(accountsRef, orderBy('code', 'asc'));
      const snapshot = await getDocs(q);

      const accountsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setAccounts(accountsList);
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Initialize COA for companies without any accounts
  const handleInitializeCOA = async () => {
    clearSystemAccountMisses(activeCompany?.id); // speed-cache safety: refresh remembered settings
    if (!activeCompany?.id || !userProfile?.uid) {
      alert('No active company or user profile found. Please ensure you are logged in.');
      return;
    }

    if (accounts.length > 0) {
      alert('Chart of Accounts already exists. Use "Repair COA" to add missing system accounts.');
      return;
    }

    if (!window.confirm('This will create the default Chart of Accounts structure. Continue?')) {
      return;
    }

    try {
      setCreatingDefaults(true);
      const result = await initializeChartOfAccounts(activeCompany.id, userProfile.uid);

      if (result.success) {
        alert(result.message);
        await loadAccounts();
        // Expand Level 1 accounts
        const level1Ids = accounts.filter(a => a.level === 1).map(a => a.id);
        setExpandedNodes(new Set(level1Ids));
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error initializing COA:', error);
      alert('Error initializing COA. Please try again.');
    } finally {
      setCreatingDefaults(false);
    }
  };

  // Repair COA — checks first, shows exactly what it intends to change, and only
  // writes after the user agrees. It used to write straight away while promising
  // "existing accounts will not be affected", which was not true.
  const handleRepairCOA = async () => {
    clearSystemAccountMisses(activeCompany?.id); // speed-cache safety: refresh remembered settings
    if (!activeCompany?.id || !userProfile?.uid) {
      alert('No active company or user profile found. Please ensure you are logged in.');
      return;
    }

    const bullets = (list, max = 12) => {
      const shown = list.slice(0, max).map(x => `   • ${x}`);
      if (list.length > max) shown.push(`   • …and ${list.length - max} more`);
      return shown.join('\n');
    };

    try {
      setCreatingDefaults(true);

      // 1. Look, don't touch.
      const preview = await repairChartOfAccounts(activeCompany.id, userProfile.uid, { dryRun: true });
      if (!preview.success) {
        alert(`Error: ${preview.message}`);
        return;
      }

      const nothingToDo =
        preview.accountsAdded === 0 &&
        preview.accountsFixed === 0 &&
        (preview.warnings || []).length === 0;

      if (nothingToDo) {
        alert('Chart of Accounts checked — everything is already correct. Nothing to repair.');
        return;
      }

      // 2. Show the plan.
      const lines = [
        'CHART OF ACCOUNTS — CHECK RESULT',
        '',
        `Accounts to create : ${preview.accountsAdded}`,
        `Accounts to correct: ${preview.accountsFixed}`
      ];
      if (preview.created?.length) {
        lines.push('', 'Will be created:', bullets(preview.created));
      }
      if (preview.changes?.length) {
        lines.push('', 'Will be corrected:', bullets(preview.changes));
      }
      if (preview.renamed?.length) {
        lines.push('', 'Names that will be RESET to the standard wording:', bullets(preview.renamed));
      }
      if (preview.warnings?.length) {
        lines.push('', 'Please note:', bullets(preview.warnings, 8));
      }
      lines.push('', 'Nothing is ever deleted. Apply these changes now?');

      if (!window.confirm(lines.join('\n'))) return;

      // 3. Apply.
      const result = await repairChartOfAccounts(activeCompany.id, userProfile.uid);

      if (result.success) {
        alert(result.message);
        await loadAccounts();
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error('Error repairing COA:', error);
      alert('Error repairing COA. Please try again.');
    } finally {
      setCreatingDefaults(false);
    }
  };

  const toggleNode = (accountId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedNodes(newExpanded);
  };

  const openAddModal = async (parent = null) => {
    // L1 (account types) cannot have user-added siblings — those 5 are the only
    // Level 1 entries the system supports. Sub-levels (L2/L3/L4/L5) are all
    // addable provided the parent isn't reserved for auto-linked entities.
    if (!parent) {
      alert('Pick a Level 1 parent (Assets / Liabilities / Equity / Revenue / Expenses) to add an account under.');
      return;
    }

    if (parent.level >= 5) {
      alert('Cannot add sub-accounts under a Level 5 (postable) account.');
      return;
    }

    if (parent.allowUserAccounts === false) {
      alert(`Cannot add accounts under "${parent.name}". This category is reserved for auto-linked accounts (Customers/Vendors/Banks/Staff).`);
      return;
    }

    const level = parent.level + 1;

    const autoCode = await generateAccountCode(
      activeCompany?.id,
      parent.id,
      parent.code,
      parent.level
    );

    setFormData({
      code: autoCode,
      name: '',
      level: level,
      parentId: parent.id,
      accountType: parent.accountType,
      balanceType: parent.balanceType,
      description: '',
      linkedEntityType: null,
      linkedEntityId: null,
      isActive: true,
      isSystemAccount: false
    });
    setParentAccount(parent);
    setEditingAccount(null);
    setShowModal(true);
  };

  const openEditModal = (account) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    // L1 account types are fixed by design — Assets/Liabilities/Equity/Revenue/Expenses.
    if (account.level === 1) {
      alert('Level 1 account types (Assets / Liabilities / Equity / Revenue / Expenses) are fixed and cannot be edited.');
      return;
    }

    // Linked accounts should be edited from their entity form (Customers/Vendors/Banks/Staff)
    if (account.linkedEntityId) {
      const entityType = account.linkedEntityType || 'entity';
      alert(`This account is linked to a ${entityType}. Please edit it from the ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}s page to keep data in sync.`);
      return;
    }

    // System accounts (L2-L5): only name and description editable. Code, type,
    // balance type, parent — locked, because automated postings resolve them
    // by code and accounting integrity depends on the structure.
    setFormData({
      code: account.code,
      name: account.name,
      level: account.level,
      parentId: account.parentId,
      accountType: account.accountType,
      balanceType: account.balanceType,
      description: account.description || '',
      linkedEntityType: account.linkedEntityType || null,
      linkedEntityId: account.linkedEntityId || null,
      isActive: account.isActive !== false,
      isSystemAccount: !!account.isSystemAccount
    });
    setEditingAccount(account);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    clearSystemAccountMisses(activeCompany?.id); // speed-cache safety: refresh remembered settings
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    // New account: the code shown was generated when the modal opened, which can
    // be stale by save time (someone else added an account under the same parent
    // meanwhile) — the classic cause of two accounts sharing one code. Re-derive
    // a fresh code from the LATEST data now, and bump past any collision, so no
    // new duplicate can form. (The code field is auto-generated/disabled, so we
    // never overwrite something the user hand-typed.)
    let codeToSave = formData.code;
    if (!editingAccount && parentAccount) {
      try {
        codeToSave = await generateAccountCode(
          activeCompany?.id, parentAccount.id, parentAccount.code, parentAccount.level
        );
        for (let attempt = 0; attempt < 6; attempt++) {
          const taken = await isAccountCodeDuplicate(activeCompany?.id, codeToSave, null);
          if (!taken) break;
          const suffix = codeToSave.slice(parentAccount.code.length);
          const width = suffix.length || 3;
          codeToSave = `${parentAccount.code}${String((parseInt(suffix, 10) || 0) + 1).padStart(width, '0')}`;
        }
      } catch (err) { codeToSave = formData.code; /* fall back to the shown code */ }
    }

    const codeDup = await isAccountCodeDuplicate(activeCompany?.id, codeToSave, editingAccount?.id);
    if (codeDup) {
      alert('This account code is already in use'); return;
    }

    try {
      // Resolve the parent from the form itself, NOT from the `parentAccount`
      // state — that state is only ever set by the Add flow. On an Edit it was
      // null (fresh page) or left over from a previous Add, so saving an edit
      // blanked the account's breadcrumb (making it vanish from the tree) or
      // filed it under the wrong branch. This was the root cause of the
      // "account exists but I can't see it" reports.
      const resolvedParent = formData.parentId
        ? (accounts.find(a => a.id === formData.parentId) || parentAccount || null)
        : null;

      const accountData = {
        ...formData,
        code: codeToSave,
        currentBalance: editingAccount?.currentBalance || 0,
        updatedAt: new Date().toISOString()
      };

      if (resolvedParent) {
        accountData.path = [...(resolvedParent.path || []), resolvedParent.id];
      } else if (editingAccount) {
        accountData.path = editingAccount.path || [];   // keep what it already had
      } else {
        accountData.path = [];
      }

      // Remember a deliberate rename of a standard account so Repair COA stops
      // silently resetting it back to the built-in wording on every run.
      if (editingAccount?.isSystemAccount && formData.name !== editingAccount.name) {
        accountData.nameCustomized = true;
      }

      if (editingAccount) {
        const accountRef = doc(db, 'companies', activeCompany?.id, 'accounts', editingAccount.id);
        await updateDoc(accountRef, accountData);

        setAccounts(prevAccounts =>
          prevAccounts.map(acc =>
            acc.id === editingAccount.id
              ? { ...acc, ...accountData }
              : acc
          )
        );
      } else {
        accountData.createdAt = new Date().toISOString();
        accountData.createdBy = userProfile.uid;
        const accountsRef = collection(db, 'companies', activeCompany?.id, 'accounts');
        const docRef = await addDoc(accountsRef, accountData);

        const newAccount = {
          id: docRef.id,
          ...accountData
        };
        setAccounts(prevAccounts => [...prevAccounts, newAccount].sort((a, b) => a.code.localeCompare(b.code)));

        if (parentAccount) {
          setExpandedNodes(prev => new Set([...prev, parentAccount.id]));
        }
      }

      invalidateMaster(activeCompany?.id, 'accounts'); // refresh forms' local cache
      setShowModal(false);
    } catch (error) {
      console.error('Error saving account:', error);
      alert('Error saving account. Please try again.');
    }
  };

  const handleDelete = async (account) => {
    if (!canDelete) { alert('You do not have permission to delete accounts.'); return; }
    // Prevent deleting system accounts
    if (account.isSystemAccount) {
      alert('Cannot delete system account. System accounts are protected and required for proper ERP functionality.');
      return;
    }

    // Prevent deleting linked accounts that still have entities
    if (account.linkedEntityId) {
      alert(`Cannot delete this account. It is linked to a ${account.linkedEntityType}. Delete the ${account.linkedEntityType} first.`);
      return;
    }

    const hasChildren = accounts.some(a => a.parentId === account.id);

    if (hasChildren) {
      alert('Cannot delete account with sub-accounts. Please delete child accounts first.');
      return;
    }

    // Check if account has related sub-transactions
    const { hasReferences, message } = await checkEntityReferences(activeCompany?.id, 'account', account.id);
    if (hasReferences) {
      alert(message);
      return;
    }

    // Check if account has transactions (balance check)
    if (account.currentBalance !== 0 && account.currentBalance !== undefined) {
      if (!window.confirm(`Warning: This account has a balance of ${formatBalance(account.currentBalance)}. Deleting it may cause accounting imbalances. Are you sure?`)) {
        return;
      }
    }

    if (window.confirm(`Are you sure you want to delete "${account.name}"?`)) {
      try {
        const accountRef = doc(db, 'companies', activeCompany?.id, 'accounts', account.id);
        await deleteDoc(accountRef);
        await writeAuditLog(activeCompany?.id, null, 'DELETE', 'account', account.id, `Deleted account: ${account.code} - ${account.name}`);
        invalidateMaster(activeCompany?.id, 'accounts');
        setAccounts(prevAccounts => prevAccounts.filter(acc => acc.id !== account.id));
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('Error deleting account. Please try again.');
      }
    }
  };

  const formatBalance = (balance) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0
    }).format(balance || 0);
  };

  // Stats calculations
  const level1Count = accounts.filter(a => a.level === 1).length;
  const level2Count = accounts.filter(a => a.level === 2).length;
  const level3Count = accounts.filter(a => a.level === 3).length;
  const level4Count = accounts.filter(a => a.level === 4).length;
  const level5Count = accounts.filter(a => a.level === 5).length;

  // L1 cards rendered in a fixed canonical order (Assets, Liabilities, Equity,
  // Revenue, Expenses) regardless of insertion order in Firestore.
  const TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'expense'];
  const l1Accounts = accounts
    .filter(a => a.level === 1)
    .sort((a, b) => TYPE_ORDER.indexOf(a.accountType) - TYPE_ORDER.indexOf(b.accountType));

  const getTypeIcon = (accountType) => {
    switch (accountType) {
      case 'asset': return <FiBriefcase />;
      case 'liability': return <FiCreditCard />;
      case 'equity': return <FiPieChart />;
      case 'revenue': return <FiTrendingUp />;
      case 'expense': return <FiTrendingDown />;
      default: return <FiBriefcase />;
    }
  };

  // Search filtering logic
  const searchLower = searchTerm.toLowerCase().trim();

  const matchingAccounts = searchLower ? accounts.filter(account =>
    account.name?.toLowerCase().includes(searchLower) ||
    account.code?.toLowerCase().includes(searchLower)
  ) : [];

  const matchingIds = new Set(matchingAccounts.map(a => a.id));

  // Get all parent IDs that need to be visible
  const getParentIds = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    if (!account) return [];
    return account.path || [];
  };

  const parentIdsToShow = new Set();
  matchingAccounts.forEach(account => {
    if (account.path) {
      account.path.forEach(pid => parentIdsToShow.add(pid));
    }
    if (account.parentId) {
      parentIdsToShow.add(account.parentId);
    }
  });

  // Auto-expand nodes when searching
  useEffect(() => {
    if (searchLower && parentIdsToShow.size > 0) {
      setExpandedNodes(prev => new Set([...prev, ...parentIdsToShow]));
    }
  }, [searchTerm]);

  const isAccountVisible = (account) => {
    if (!searchLower) return true;
    if (matchingIds.has(account.id)) return true;

    const hasMatchingDescendant = accounts.some(a =>
      matchingIds.has(a.id) && (a.path?.includes(account.id) || a.parentId === account.id)
    );
    return hasMatchingDescendant;
  };

  const isAccountMatch = (account) => {
    if (!searchLower) return false;
    return account.name?.toLowerCase().includes(searchLower) ||
           account.code?.toLowerCase().includes(searchLower);
  };

  const getChildCount = (accountId) => {
    return accounts.filter(a => a.parentId === accountId).length;
  };

  const renderAccountTree = (parentId = null, level = 1) => {
    const childAccounts = accounts.filter(a => a.parentId === parentId);
    if (childAccounts.length === 0) return null;

    const visibleAccounts = childAccounts.filter(account => isAccountVisible(account));
    if (visibleAccounts.length === 0) return null;

    return visibleAccounts.map(account => {
      const hasChildren = accounts.some(a => a.parentId === account.id);
      const isExpanded = expandedNodes.has(account.id);
      const isMatch = isAccountMatch(account);
      const childCount = getChildCount(account.id);
      const isSystem = account.isSystemAccount;
      const isLinked = account.linkedEntityId;
      const canAddChildren = level < 5 && account.allowUserAccounts !== false;

      return (
        <AccountNode key={account.id}>
          <AccountHeader $level={level} $isMatch={isMatch} $isSystem={isSystem}>
            <AccountLeft onClick={() => hasChildren && toggleNode(account.id)}>
              {hasChildren ? (
                isExpanded ? <FiChevronDown /> : <FiChevronRight />
              ) : (
                <span style={{ width: 16, display: 'inline-block' }} />
              )}
              {level === 5 ? (
                <PostableDot title="Postable account" />
              ) : (
                <FolderIcon><FiFolder size={14} /></FolderIcon>
              )}
              <AccountCode $isMatch={isMatch} $level={level}>{account.code}</AccountCode>
              <AccountName $isMatch={isMatch} $level={level}>{account.name}</AccountName>
              {level === 5 && <PostableBadge>POSTABLE</PostableBadge>}
              {level < 5 && <CategoryBadge>CATEGORY · L{level}</CategoryBadge>}
              {isSystem && (
                <SystemBadge title="System account — Protected">
                  <FiLock />
                </SystemBadge>
              )}
              {isLinked && (
                <LinkedBadge title={`Linked to ${account.linkedEntityType}`}>
                  {account.linkedEntityType}
                </LinkedBadge>
              )}
              {hasChildren && (
                <AccountCount>{childCount} sub-{childCount === 1 ? 'account' : 'accounts'}</AccountCount>
              )}
            </AccountLeft>
            <AccountRight>
              {level === 5 && (
                <AccountType $type={account.accountType}>{account.accountType}</AccountType>
              )}
              <AccountActions>
                {canAddChildren && canEdit && (
                  <ActionButton onClick={(e) => { e.stopPropagation(); openAddModal(account); }} title="Add Sub-Account">
                    <FiPlus /> Add
                  </ActionButton>
                )}
                {canChange && (
                  <ActionButton onClick={(e) => { e.stopPropagation(); openEditModal(account); }} title={isSystem ? "Edit (Limited)" : "Edit"}>
                    <FiEdit2 /> Edit
                  </ActionButton>
                )}
                {!isSystem && canDelete && (
                  <ActionButton $danger onClick={(e) => { e.stopPropagation(); handleDelete(account); }} title="Delete">
                    <FiTrash2 />
                  </ActionButton>
                )}
              </AccountActions>
            </AccountRight>
          </AccountHeader>

          {isExpanded && hasChildren && (
            <AccountChildren>
              {renderAccountTree(account.id, level + 1)}
            </AccountChildren>
          )}
        </AccountNode>
      );
    });
  };

  return (
    <PageLayout pageTitle="Chart of Accounts" breadcrumb="Master Data / Chart of Accounts">
      <Container>
        <HeaderSection>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search by account code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')}>
                <FiX />
              </ClearButton>
            )}
          </SearchBox>
          {searchLower && (
            <SearchResultsInfo>
              {matchingAccounts.length} result{matchingAccounts.length !== 1 ? 's' : ''} found
            </SearchResultsInfo>
          )}
          <ButtonGroup>
            <PrintBtn onClick={() => printChartOfAccounts(accounts, { activeCompany })} disabled={accounts.length === 0}>
              <FiPrinter /> Print
            </PrintBtn>
            {canEdit && (accounts.length === 0 ? (
              <InitButton onClick={handleInitializeCOA} disabled={creatingDefaults}>
                <FiDownload /> {creatingDefaults ? 'Initializing...' : 'Initialize COA'}
              </InitButton>
            ) : (
              <RepairButton onClick={handleRepairCOA} disabled={creatingDefaults}>
                <FiTool /> {creatingDefaults ? 'Repairing...' : 'Repair COA'}
              </RepairButton>
            ))}
          </ButtonGroup>
        </HeaderSection>

        <StatsRow>
          <StatCard>
            <StatIcon $color={colors.primary.main}><FiFolder /></StatIcon>
            <StatInfo>
              <StatValue>{level1Count}</StatValue>
              <StatLabel>Main Groups</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.success.main}><FiLayers /></StatIcon>
            <StatInfo>
              <StatValue>{level2Count + level3Count}</StatValue>
              <StatLabel>Sub Groups</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.info.main}><FiFileText /></StatIcon>
            <StatInfo>
              <StatValue>{level4Count + level5Count}</StatValue>
              <StatLabel>Detail Accounts</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.warning.main}><FiLayers /></StatIcon>
            <StatInfo>
              <StatValue>{accounts.length}</StatValue>
              <StatLabel>Total Accounts</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsRow>

        {loading ? (
          <LoadingMessage>Loading accounts...</LoadingMessage>
        ) : accounts.length === 0 ? (
          <EmptyState>
            No accounts found. Click <strong>Initialize COA</strong> to create the default chart of accounts.
          </EmptyState>
        ) : (
          <TypeCardStack>
            {l1Accounts.map(l1 => {
              const isExpandedRaw = expandedNodes.has(l1.id);
              const hasMatchInside = searchLower && accounts.some(a =>
                (a.path?.includes(l1.id) || a.id === l1.id) && isAccountMatch(a)
              );
              const isExpanded = isExpandedRaw || hasMatchInside;
              const leafCount = accounts.filter(a => a.level === 5 && a.path?.includes(l1.id)).length;
              const isVisible = !searchLower || hasMatchInside || isAccountMatch(l1);
              if (!isVisible) return null;

              return (
                <TypeCard key={l1.id} $type={l1.accountType}>
                  <TypeCardHeader
                    $type={l1.accountType}
                    $expanded={isExpanded}
                    onClick={() => toggleNode(l1.id)}
                  >
                    <TypeHeaderLeft>
                      <ChevronIcon>
                        {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                      </ChevronIcon>
                      <TypeIcon $type={l1.accountType}>
                        {getTypeIcon(l1.accountType)}
                      </TypeIcon>
                      <div>
                        <TypeName>{l1.name}</TypeName>
                        <TypeMeta>{leafCount} {leafCount === 1 ? 'account' : 'accounts'}</TypeMeta>
                      </div>
                    </TypeHeaderLeft>
                  </TypeCardHeader>
                  {isExpanded && (
                    <TypeCardBody>
                      {renderAccountTree(l1.id, 2)}
                    </TypeCardBody>
                  )}
                </TypeCard>
              );
            })}
          </TypeCardStack>
        )}
      </Container>

      {/* Modal */}
      {showModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>
                {editingAccount ? 'Edit Account' : `Add Level ${formData.level} Account`}
                {parentAccount && ` under "${parentAccount.name}"`}
              </h3>
              <CloseButton onClick={() => setShowModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormRow>
                  <FormGroup>
                    <label>Account Code *</label>
                    <input
                      type="text"
                      value={formData.code}
                      placeholder="Auto-generated"
                      required
                      disabled
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Account Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter account name"
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <label>Account Type</label>
                    <select
                      value={formData.accountType}
                      onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                      disabled
                    >
                      <option value="asset">Asset</option>
                      <option value="liability">Liability</option>
                      <option value="equity">Capital/Equity</option>
                      <option value="revenue">Revenue</option>
                      <option value="expense">Expense</option>
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <label>Balance Type</label>
                    <select
                      value={formData.balanceType}
                      onChange={(e) => setFormData({ ...formData, balanceType: e.target.value })}
                      disabled
                    >
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                    </select>
                  </FormGroup>
                </FormRow>

                {/* Status toggle is available for any non-system account.
                    System accounts stay active by design — automated postings
                    can target them whether the user marked them inactive or not. */}
                {!formData.isSystemAccount && (
                  <FormRow>
                    <FormGroup>
                      <label>Status</label>
                      <select
                        value={formData.isActive ? 'active' : 'inactive'}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      <HelpText>
                        Opening balances are entered from the Opening Balances page.
                        For linked accounts (Customers/Vendors/Banks/Staff), use their respective forms.
                      </HelpText>
                    </FormGroup>
                  </FormRow>
                )}

                <FormGroup>
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional notes or description"
                    rows={3}
                  />
                </FormGroup>

                <ModalFooter>
                  <CancelButton type="button" onClick={() => setShowModal(false)}>Cancel</CancelButton>
                  <SaveButton type="submit">
                    <FiSave /> {editingAccount ? 'Update' : 'Create'}
                  </SaveButton>
                </ModalFooter>
              </form>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  flex: 1;
  max-width: 400px;

  svg {
    color: ${colors.text.secondary};
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: ${typography.fontSize.sm};
    background: transparent;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.hover};
  }
`;

const SearchResultsInfo = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  white-space: nowrap;
  padding: 0 ${spacing.sm};
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

const PrintBtn = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: white;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}50;
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: ${colors.primary.main}10; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const InitButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.success.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.success.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RepairButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.warning.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.warning.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing.md};
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: ${colors.background.default};
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: ${typography.fontSize.h4};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;

const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  background: ${colors.background.default};
  border: 1px dashed ${colors.border.main};
  border-radius: ${borderRadius.lg};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.md};
`;

const AccountNode = styled.div`
  /* Subtle hairline between sibling rows, no per-level coloring. */
  & + & { border-top: 1px solid ${colors.border.light}; }
`;

const AccountHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.md};
  padding: ${props => props.$level === 5 ? '10px' : '12px'} ${spacing.md};
  padding-left: ${props => (props.$level - 2) * 20 + 14}px;
  /* Categories (L2/L3/L4) get a subtle grey wash; postable L5s stay clean white.
     A search-match overrides everything with a warm yellow. */
  background: ${props => {
    if (props.$isMatch) return colors.warning.main + '15';
    if (props.$level === 5) return 'white';
    if (props.$level === 2) return '#F1F3F5';   /* darker for top categories */
    if (props.$level === 3) return '#F6F8FA';
    return '#FAFBFC';                            /* L4 — lightest grey */
  }};
  border-left: ${props => {
    if (props.$isMatch) return `3px solid ${colors.warning.main}`;
    if (props.$level === 5) return `3px solid ${colors.success.main}40`;
    return '3px solid transparent';
  }};
  transition: background 0.15s ease;

  &:hover {
    background: ${props => props.$isMatch ? colors.warning.main + '25' : colors.neutral?.[50] || '#fafafa'};
  }
`;

const AccountLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  cursor: pointer;
  flex: 1;

  > svg:first-child {
    color: ${colors.text.secondary};
    min-width: 16px;
  }

  > svg:nth-child(2) {
    color: ${colors.primary.main};
    min-width: 16px;
  }
`;

const AccountCode = styled.span`
  font-size: ${typography.fontSize.xs};
  font-family: monospace;
  /* Postable L5 codes get the primary brand color so they pop. Category codes
     stay neutral so the eye is drawn to the postable rows. */
  color: ${props => {
    if (props.$isMatch) return colors.warning.dark;
    if (props.$level === 5) return colors.primary.main;
    return colors.text.tertiary;
  }};
  background: ${props => {
    if (props.$isMatch) return colors.warning.main + '30';
    if (props.$level === 5) return colors.primary.main + '10';
    return colors.neutral?.[100] || '#F1F3F5';
  }};
  padding: 3px 8px;
  border-radius: ${borderRadius.sm};
  font-weight: ${props => props.$isMatch ? typography.fontWeight.semibold : props.$level === 5 ? typography.fontWeight.semibold : 'normal'};
  min-width: 64px;
  text-align: center;
`;

const AccountName = styled.span`
  /* Categories read as headings; postable accounts read as data rows. */
  font-weight: ${props => {
    if (props.$isMatch) return typography.fontWeight.bold;
    if (props.$level === 5) return typography.fontWeight.regular || 400;
    return typography.fontWeight.semibold;
  }};
  color: ${props => props.$isMatch ? colors.warning.dark : colors.text.primary};
  font-size: ${props => props.$level === 5 ? typography.fontSize.sm : typography.fontSize.md};
  text-transform: ${props => props.$level === 2 ? 'uppercase' : 'none'};
  letter-spacing: ${props => props.$level === 2 ? '0.5px' : '0'};
`;

const AccountCount = styled.span`
  font-size: 11px;
  color: ${colors.text.tertiary};
  font-style: italic;
`;

const PostableDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.success.main};
  box-shadow: 0 0 0 3px ${colors.success.main}25;
  flex-shrink: 0;
`;

const FolderIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${colors.text.tertiary};
  flex-shrink: 0;
`;

const PostableBadge = styled.span`
  font-size: 9px;
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: 0.6px;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${colors.success.main}18;
  color: ${colors.success.dark || colors.success.main};
  text-transform: uppercase;
`;

const CategoryBadge = styled.span`
  font-size: 9px;
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: 0.6px;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${colors.neutral?.[200] || '#E9ECEF'};
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const SystemBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.info.main};
  font-size: 12px;
  margin-left: 4px;

  svg {
    font-size: 12px;
  }
`;

const LinkedBadge = styled.span`
  font-size: 10px;
  padding: 1px 6px;
  border-radius: ${borderRadius.sm};
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  text-transform: capitalize;
  margin-left: 4px;
`;

const AccountRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const AccountType = styled.span`
  font-size: ${typography.fontSize.xs};
  padding: 2px 8px;
  border-radius: ${borderRadius.sm};
  text-transform: capitalize;
  background: ${props => {
    const typeColors = {
      asset: colors.success.main + '15',
      liability: colors.warning.main + '15',
      equity: colors.info.main + '15',
      revenue: colors.success.main + '15',
      expense: colors.error.main + '15'
    };
    return typeColors[props.$type] || colors.background.paper;
  }};
  color: ${props => {
    const typeColors = {
      asset: colors.success.main,
      liability: colors.warning.main,
      equity: colors.info.main,
      revenue: colors.success.main,
      expense: colors.error.main
    };
    return typeColors[props.$type] || colors.text.secondary;
  }};
`;

const AccountActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  opacity: 0.5;
  transition: opacity 0.15s ease;

  ${AccountHeader}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  /* Text buttons take their natural width; the Delete (icon-only) button
     still renders square via the &.icon-only flag below. */
  min-height: 30px;
  padding: ${props => props.$danger ? '0' : '4px 12px'};
  width: ${props => props.$danger ? '30px' : 'auto'};
  background: ${props => props.$danger ? 'transparent' : colors.primary.main + '12'};
  color: ${props => props.$danger ? colors.error.main : colors.primary.main};
  border: 1px solid ${props => props.$danger ? 'transparent' : colors.primary.main + '25'};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  svg { width: 14px; height: 14px; }

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '15' : colors.primary.main + '22'};
    color: ${props => props.$danger ? colors.error.main : colors.primary.dark || colors.primary.main};
  }
`;

const AccountChildren = styled.div`
  /* Indent guide rendered as a thin gutter line so nesting reads clearly. */
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 8px;
    left: 24px;
    width: 1px;
    background: ${colors.border.light};
  }
`;

/* ─── Account Type Cards (accordion view) ───────────────────────────────── */
const TYPE_PALETTE = {
  asset:     { main: '#2E7D32', tint: '#E8F5E9' },
  liability: { main: '#EF6C00', tint: '#FFF3E0' },
  equity:    { main: '#1565C0', tint: '#E3F2FD' },
  revenue:   { main: '#00838F', tint: '#E0F7FA' },
  expense:   { main: '#C62828', tint: '#FFEBEE' },
};

const TypeCardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;
const TypeCard = styled.div`
  background: white;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  transition: box-shadow 0.18s ease;
  &:hover { box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04); }
`;
const TypeCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.md};
  padding: 16px 20px;
  cursor: pointer;
  background: ${p => (TYPE_PALETTE[p.$type]?.tint || colors.background.paper)};
  border-bottom: ${p => p.$expanded
    ? `1px solid ${(TYPE_PALETTE[p.$type]?.main || colors.primary.main)}30`
    : '1px solid transparent'};
  transition: filter 0.15s ease;
  &:hover { filter: brightness(0.97); }
`;
const TypeHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
`;
const ChevronIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  font-size: 18px;
  svg { width: 18px; height: 18px; }
`;
const TypeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: white;
  color: ${p => (TYPE_PALETTE[p.$type]?.main || colors.primary.main)};
  border: 1px solid ${p => (TYPE_PALETTE[p.$type]?.main || colors.primary.main)}30;
  svg { width: 18px; height: 18px; }
`;
const TypeName = styled.div`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  letter-spacing: 0.2px;
`;
const TypeMeta = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  margin-top: 2px;
`;
const TypeCardBody = styled.div`
  background: white;
  padding: 4px 0;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.md};
`;

const Modal = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};

  h3 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
    margin: 0;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${spacing.lg};

  form {
    display: flex;
    flex-direction: column;
    gap: ${spacing.md};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  label {
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.secondary};
  }

  input, select, textarea {
    padding: ${spacing.sm} ${spacing.md};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
    background: ${colors.background.default};
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
      box-shadow: 0 0 0 3px ${colors.primary.main}20;
    }

    &:disabled {
      background: ${colors.background.paper};
      cursor: not-allowed;
    }
  }

  textarea {
    resize: vertical;
  }
`;

const HelpText = styled.p`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  font-style: italic;
  margin-top: ${spacing.xs};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  margin-top: ${spacing.md};
  padding-top: ${spacing.md};
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: transparent;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    border-color: ${colors.text.secondary};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

export default ChartOfAccounts;
